import { useState, useEffect, Fragment } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { X, Users, Crown, Shield, Trash2, UserMinus, Mail, Settings } from 'lucide-react'
import { apiClient, handleApiError } from '@/lib/api'

interface TeamMember {
  membership_id: number
  user: {
    id: number
    name: string
    email: string
    avatar_url?: string
    role: string
  }
  team_role: string
  status: string
  joined_at: string
  added_by: string
}

interface TeamDetails {
  team: {
    id: number
    name: string
    description: string
    team_type: string
    max_members: number
    is_active: boolean
    created_at: string
    settings?: any
    team_lead: {
      id: number
      name: string
      email: string
    } | null
  }
  members: TeamMember[]
}

interface Team {
  id: number
  name: string
  description: string
  team_type: string
}

interface TeamDetailsModalProps {
  open: boolean
  onClose: () => void
  team: Team | null
  onTeamUpdated: () => void
}

export default function TeamDetailsModal({ open, onClose, team, onTeamUpdated }: TeamDetailsModalProps) {
  const [teamDetails, setTeamDetails] = useState<TeamDetails | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (open && team) {
      loadTeamDetails()
    }
  }, [open, team])

  const loadTeamDetails = async () => {
    if (!team) return

    try {
      setLoading(true)
      setError('')
      const response = await apiClient.getTeamDetails(team.id.toString())
      
      if (response.success) {
        setTeamDetails(response.data as any)
      } else {
        setError('Failed to load team details')
      }
    } catch (err) {
      setError('Failed to load team details: ' + handleApiError(err))
    } finally {
      setLoading(false)
    }
  }

  const handleRemoveMember = async (memberId: number, memberName: string) => {
    if (!team || !teamDetails) return

    if (!window.confirm(`Are you sure you want to remove ${memberName} from the team?`)) {
      return
    }

    try {
      const response = await apiClient.removeTeamMember(team.id.toString(), memberId)
      
      if (response.success) {
        // Reload team details
        await loadTeamDetails()
        onTeamUpdated()
      } else {
        setError(response.message || 'Failed to remove member')
      }
    } catch (err) {
      setError('Failed to remove member: ' + handleApiError(err))
    }
  }

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'lead': return <Crown className="w-4 h-4 text-yellow-600" />
      case 'admin': return <Shield className="w-4 h-4 text-blue-600" />
      default: return <Users className="w-4 h-4 text-gray-500" />
    }
  }

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'lead': return 'bg-yellow-100 text-yellow-800'
      case 'admin': return 'bg-blue-100 text-blue-800'
      case 'viewer': return 'bg-gray-100 text-gray-800'
      default: return 'bg-green-100 text-green-800'
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800'
      case 'inactive': return 'bg-gray-100 text-gray-800'
      case 'suspended': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  if (!team) return null

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title
                  as="h3"
                  className="text-lg font-medium leading-6 text-gray-900 flex items-center justify-between mb-6"
                >
                  <div className="flex items-center space-x-2">
                    <Users className="w-5 h-5 text-blue-600" />
                    <span>{team.name}</span>
                  </div>
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </Dialog.Title>

                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <span className="ml-2 text-gray-600">Loading team details...</span>
                  </div>
                ) : error ? (
                  <div className="text-center py-8">
                    <p className="text-red-600">{error}</p>
                    <button
                      onClick={loadTeamDetails}
                      className="mt-2 text-blue-600 hover:text-blue-800"
                    >
                      Try Again
                    </button>
                  </div>
                ) : teamDetails ? (
                  <div className="space-y-6">
                    {/* Team Info */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <h4 className="text-sm font-medium text-gray-500">Type</h4>
                          <p className="text-sm text-gray-900 capitalize">{teamDetails.team.team_type}</p>
                        </div>
                        <div>
                          <h4 className="text-sm font-medium text-gray-500">Members</h4>
                          <p className="text-sm text-gray-900">
                            {teamDetails.members.length} / {teamDetails.team.max_members}
                          </p>
                        </div>
                        <div>
                          <h4 className="text-sm font-medium text-gray-500">Status</h4>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${teamDetails.team.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                            {teamDetails.team.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        <div>
                          <h4 className="text-sm font-medium text-gray-500">Created</h4>
                          <p className="text-sm text-gray-900">
                            {new Date(teamDetails.team.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      {teamDetails.team.description && (
                        <div className="mt-4">
                          <h4 className="text-sm font-medium text-gray-500">Description</h4>
                          <p className="text-sm text-gray-900 mt-1">{teamDetails.team.description}</p>
                        </div>
                      )}
                    </div>

                    {/* Team Lead */}
                    {teamDetails.team.team_lead && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-2 flex items-center">
                          <Crown className="w-4 h-4 mr-2 text-yellow-600" />
                          Team Lead
                        </h4>
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                          <div className="flex items-center">
                            <div className="w-8 h-8 bg-yellow-200 rounded-full flex items-center justify-center">
                              <span className="text-yellow-800 text-sm font-medium">
                                {teamDetails.team.team_lead.name.charAt(0)}
                              </span>
                            </div>
                            <div className="ml-3">
                              <p className="text-sm font-medium text-gray-900">
                                {teamDetails.team.team_lead.name}
                              </p>
                              <p className="text-sm text-gray-600">
                                {teamDetails.team.team_lead.email}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Team Members */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 mb-3">
                        Team Members ({teamDetails.members.length})
                      </h4>
                      
                      {teamDetails.members.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                          <Users className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                          <p>No team members yet</p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {teamDetails.members.map((member) => (
                            <div
                              key={member.membership_id}
                              className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50"
                            >
                              <div className="flex items-center space-x-3">
                                <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                                  <span className="text-gray-600 text-sm font-medium">
                                    {member.user.name.charAt(0)}
                                  </span>
                                </div>
                                <div>
                                  <div className="flex items-center space-x-2">
                                    <p className="text-sm font-medium text-gray-900">
                                      {member.user.name}
                                    </p>
                                    {getRoleIcon(member.team_role)}
                                  </div>
                                  <p className="text-sm text-gray-600">{member.user.email}</p>
                                  <div className="flex items-center space-x-2 mt-1">
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(member.team_role)}`}>
                                      {member.team_role}
                                    </span>
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(member.status)}`}>
                                      {member.status}
                                    </span>
                                  </div>
                                </div>
                              </div>

                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() => handleRemoveMember(member.membership_id, member.user.name)}
                                  className="text-red-600 hover:text-red-800 p-1"
                                  title="Remove from team"
                                >
                                  <UserMinus className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ) : null}

                <div className="mt-6 flex justify-end">
                  <button
                    onClick={onClose}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                  >
                    Close
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}
